import { AdditionalServiceType } from "../../../Types/additionalServiceType"
import { Form, Formik } from "formik"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField"
import { DateInputField } from "../../FormFields/DateInput/DateInputField"
import { Button, Col, Row } from "react-bootstrap"
import { useState } from "react"
import SelectUserModal from "../../Modals/SelectUserModal/selectUserModal"
import { SelectPropertyModal } from "../../Modals/SelectPropertyModal/selectPropertyModal"
import useUserRefs from "../../../Hooks/UseUsersQuery/UseUserRefs"
import { useSelector } from "react-redux"
import { accountSelector } from "../../../redux/account/accountSlice"

const generateBlankAdditionalService = () => ({
  serviceDate: new Date(),
  serviceEndDate: null,
  createdDate: new Date(),
  createdBy: '',
  assignedTo: '',
  propertyId: '',
  bookingId: '',
  amenityId: '',
  serviceType: '',
  serviceSubType: '',
  price: 0,
  status: 'pending',
  customerNote: '',
  internalNote: ''
})

export default function AdditionalServiceForm(props: {
  disabled: boolean,
  onSubmit: (v: any) => Promise<any>,
  initialValues?: Partial<AdditionalServiceType>,
}) {
  const {
    disabled,
    onSubmit,
    initialValues,
  } = props

  const { _id: currentUserId } = useSelector(accountSelector)
  const userRefs = useUserRefs()
  const [selectAssignedUserModal, setSelectAssignedUserModal] = useState(false)
  const [selectPropertyModal, setSelectPropertyModal] = useState(false)

  return (
    <div className="additional-service-form">
      <Formik
        initialValues={{
          ...generateBlankAdditionalService(),
          ...(initialValues ? initialValues : {}),
          // Set createdBy to current user's ID if not already set
          createdBy: initialValues?.createdBy || currentUserId || '',
        }}
        validate={values => {
          const errors: any = {};

          if (!values.propertyId) {
            errors.propertyId = 'Property is required';
          }

          if (!values.serviceType) {
            errors.serviceType = 'Service type is required';
          }

          if (!values.status) {
            errors.status = 'Status is required';
          }

          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          if (onSubmit) {
            try {
              onSubmit({ ...values })
            } catch (err) {
              // custom error handler
            }
          }
          setSubmitting(false)
        }}
      >
        {({ values, isSubmitting, errors, touched, setFieldValue, handleChange, setValues, validateForm }) => {
          return (
            <>
              <Form>
                <h3>🏠 Property Selection</h3>
                <TextInputField
                  fieldType="text"
                  fieldLabel="Property ID"
                  placeholder="Property ID"
                  fieldName="propertyId"
                  disabled={true}
                />
                <div className="mb-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSelectPropertyModal(true)}
                    disabled={disabled}
                  >
                    Select Property
                  </Button>
                  {values.propertyId && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="ms-2"
                      onClick={() => {
                        setFieldValue('propertyId', '')
                      }}
                      disabled={disabled}
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <h3>📋 Service Information</h3>
                <TextInputField
                  fieldType="text"
                  fieldLabel="Booking ID (Optional)"
                  placeholder="Booking ID"
                  fieldName="bookingId"
                />

                <TextInputField
                  fieldType="text"
                  fieldLabel="Amenity ID (Optional)"
                  placeholder="Amenity ID"
                  fieldName="amenityId"
                />

                <DateInputField
                  fieldLabel="Service Date"
                  placeholder="Service Date"
                  fieldName="serviceDate"
                />

                <DateInputField
                  fieldLabel="Service Date End"
                  placeholder="Service Date End"
                  fieldName="serviceEndDate"
                />

                {/* additional service full cleaning button */}
                <Button
                  className={"me-2 mb-2"}
                  variant={"secondary"}
                  size={"sm"}
                  disabled={(values.serviceType === 'cleaning' && values.serviceSubType === 'full') || disabled}
                  onClick={() => {
                    setFieldValue('serviceSubType', 'full', true)
                    setFieldValue('serviceType', 'cleaning', true)
                  }}>
                  Full Cleaning
                </Button>
                {/* additional service basic cleaning button */}
                <Button
                  className={"me-2 mb-2"}
                  variant={"secondary"}
                  size={"sm"}
                  disabled={(values.serviceType === 'cleaning' && values.serviceSubType === 'basic') || disabled}
                  onClick={() => {
                    setFieldValue('serviceSubType', 'basic', true)
                    setFieldValue('serviceType', 'cleaning', true)
                  }}>
                  Basic Cleaning
                </Button>

                <Row>
                  <Col xs={12} md={6}>
                    <TextInputField
                      fieldType="text"
                      fieldLabel="Service Type"
                      placeholder="e.g., Cleaning, Maintenance, Repair"
                      fieldName="serviceType"
                    />
                  </Col>

                  <Col xs={12} md={6}>
                    {/* <TextInputField
                      fieldType="text"
                      fieldLabel="Service Sub Type"
                      placeholder="e.g., Deep Cleaning, Plumbing, Electrical"
                      fieldName="serviceSubType"
                    /> */}
                    <SelectInputField
                      fieldLabel="Service Sub Type"
                      placeholder="Select Service Sub Type"
                      fieldName="serviceSubType"
                      options={[
                        { value: 'basic', text: 'Basic (turndown)' },
                        { value: 'full', text: 'Full (turnover)' },
                        // { value: 'deep', text: 'Deep' },
                      ]}
                    />
                  </Col>
                </Row>

                <TextInputField
                  fieldType="number"
                  fieldLabel="Price"
                  placeholder="Service Price"
                  fieldName="price"
                />

                <SelectInputField
                  fieldLabel="Status"
                  placeholder="Select Status"
                  fieldName="status"
                  options={[
                    { value: 'request', text: 'Request' },
                    { value: 'new', text: 'New' },
                    { value: 'pending', text: 'Pending' },
                    { value: 'started', text: 'Started' },
                    // { value: 'in-progress', text: 'In Progress' },
                    { value: 'completed', text: 'Completed' },
                    // { value: 'confirmed', text: 'Confirmed' },
                    { value: 'ready', text: 'Ready' },
                    { value: 'canceled', text: 'Canceled' }
                  ]}
                />

                <h3>👥 Assignment</h3>
                <TextInputField
                  fieldType="text"
                  fieldLabel="Created By"
                  placeholder="Created By"
                  fieldName="createdBy"
                  disabled={true}
                />
                {values.createdBy && userRefs[values.createdBy] && (
                  <div className="mb-3">
                    <small className="text-muted">
                      Created By: <strong>{userRefs[values.createdBy].firstName} {userRefs[values.createdBy].lastName}</strong> ({userRefs[values.createdBy].email})
                    </small>
                  </div>
                )}

                <TextInputField
                  fieldType="text"
                  fieldLabel="Assigned To"
                  placeholder="Assigned To (Optional)"
                  fieldName="assignedTo"
                  disabled={true}
                />
                {values.assignedTo && userRefs[values.assignedTo] && (
                  <div className="mb-3">
                    <small className="text-muted">
                      Assigned To: <strong>{userRefs[values.assignedTo].firstName} {userRefs[values.assignedTo].lastName}</strong> ({userRefs[values.assignedTo].email})
                    </small>
                  </div>
                )}
                <div className="mb-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSelectAssignedUserModal(true)}
                    disabled={disabled}
                  >
                    Assign User
                  </Button>
                  {values.assignedTo && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="ms-2"
                      onClick={() => {
                        setFieldValue('assignedTo', '')
                      }}
                      disabled={disabled}
                    >
                      Clear Assignment
                    </Button>
                  )}
                </div>

                <h3>📝 Notes</h3>
                <TextInputField
                  fieldType="textarea"
                  fieldLabel="Customer Note"
                  placeholder="Notes visible to customer"
                  fieldName="customerNote"
                />

                <TextInputField
                  fieldType="textarea"
                  fieldLabel="Internal Note"
                  placeholder="Internal notes (not visible to customer)"
                  fieldName="internalNote"
                />

                <DateInputField
                  fieldLabel="Created Date"
                  placeholder="Created Date"
                  fieldName="createdDate"
                />

                <hr />
                <div className="mb-3">
                  Errors: {JSON.stringify(errors)}
                </div>

                <Button
                  className="mb-3"
                  type="submit"
                  disabled={Object.keys(errors).length > 0 || isSubmitting || disabled}
                >
                  Submit
                </Button>

              </Form>

              <SelectPropertyModal
                active={selectPropertyModal}
                onClose={() => {
                  setSelectPropertyModal(false)
                }}
                title="Select Property"
                message="Please select a property for this additional service:"
                onSelect={(selectedProperty: any) => {
                  console.log('Selected property:', selectedProperty)
                  if (selectedProperty._id) {
                    setFieldValue('propertyId', selectedProperty._id)
                  }
                  setSelectPropertyModal(false)
                }}
              />

              <SelectUserModal
                active={selectAssignedUserModal}
                onClose={() => {
                  setSelectAssignedUserModal(false)
                }}
                title="Assign User"
                message="Please select a user to assign this service to:"
                onSelect={(selectedUser: any) => {
                  console.log('Selected user for assignment:', selectedUser)
                  if (selectedUser._id) {
                    setFieldValue('assignedTo', selectedUser._id)
                  }
                  setSelectAssignedUserModal(false)
                }}
              />
            </>
          )
        }}
      </Formik>
    </div>
  )
}
